import { supabase } from "@/lib/supabase";

export const RECORDINGS_BUCKET = "home-twice-recordings";

type SupabaseLifecycleClient = {
  from: (table: string) => any;
  storage: { from: (bucket: string) => any };
  rpc: (functionName: string, args: Record<string, unknown>) => Promise<any>;
};

function asLifecycleClient(client: unknown): SupabaseLifecycleClient {
  return client as SupabaseLifecycleClient;
}

export class RecordingStorageDeletionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RecordingStorageDeletionError";
  }
}

export class RecordingSaveOutcomeUnknownError extends Error {
  constructor() {
    super("The save response was interrupted, so the recording could not be confirmed. Refresh the page before trying again.");
    this.name = "RecordingSaveOutcomeUnknownError";
  }
}

export type SavedRecording = {
  id: string;
  file_path: string;
  mime_type: string;
  duration_seconds: number;
};

/** Remove every audio object for this owner/page before deleting rows.
 * If Storage reports an error, the database delete is intentionally skipped.
 */
export async function deletePageRecordings(
  client: unknown,
  userId: string,
  pageNumber: string,
): Promise<number> {
  const db = asLifecycleClient(client);
  const { data, error: readError } = await db
    .from("recordings")
    .select("id,file_path")
    .eq("user_id", userId)
    .eq("page_number", pageNumber);

  if (readError) throw readError;

  const rows = (data ?? []) as Array<{ id: string; file_path: string }>;
  const folderPath = `${userId}/${pageNumber}`;
  let storagePaths: string[];
  try {
    storagePaths = await listFolderFiles(db, folderPath);
  } catch (error) {
    throw new RecordingStorageDeletionError(error instanceof Error ? error.message : "Storage listing failed");
  }
  const ownerPagePrefix = `${folderPath}/`;
  if (rows.some((row) => !row.file_path.startsWith(ownerPagePrefix))) {
    throw new RecordingStorageDeletionError("A recording path is outside this account's page folder.");
  }
  const filePaths = Array.from(new Set([
    ...rows.map((row) => row.file_path),
    ...storagePaths,
  ].filter((path) => path.startsWith(ownerPagePrefix))));

  if (filePaths.length > 0) {
    const { error: storageError } = await db.storage
      .from(RECORDINGS_BUCKET)
      .remove(filePaths);
    if (storageError) {
      throw new RecordingStorageDeletionError(storageError.message);
    }
  }

  const recordingIds = rows.map((row) => row.id).filter(Boolean);
  if (recordingIds.length > 0) {
    const { error: deleteError } = await db
      .from("recordings")
      .delete()
      .in("id", recordingIds)
      .eq("user_id", userId);

    if (deleteError) throw deleteError;
  }
  return rows.length;
}

async function listFolderFiles(db: SupabaseLifecycleClient, folder: string): Promise<string[]> {
  const paths: string[] = [];
  async function walk(currentFolder: string): Promise<void> {
    let offset = 0;
    while (true) {
      const { data, error } = await db.storage
        .from(RECORDINGS_BUCKET)
        .list(currentFolder, { limit: 1000, offset, sortBy: { column: "name", order: "asc" } });
      if (error) throw error;
      const entries = data ?? [];
      for (const entry of entries) {
        if (!entry.name) continue;
        const path = `${currentFolder}/${entry.name}`;
        if (entry.id === null && entry.metadata === null) await walk(path);
        else paths.push(path);
      }
      if (entries.length < 1000) break;
      offset += 1000;
    }
  }
  await walk(folder);
  return paths;
}

/**
 * Remove prior audio after the database RPC has committed the replacement,
 * moved any active share row, and deleted the old recording rows.
 * Cleanup failure is deliberately non-fatal; these paths then appear in the
 * read-only orphan report.
 */
export async function removeReplacedAudio(
  client: unknown,
  filePaths: string[],
  currentFilePath: string,
): Promise<void> {
  const db = asLifecycleClient(client);
  const obsoletePaths = Array.from(new Set(filePaths.filter((path) => path && path !== currentFilePath)));
  if (!obsoletePaths.length) return;

  const { error } = await db.storage
    .from(RECORDINGS_BUCKET)
    .remove(obsoletePaths);
  if (error) throw error;
}

/**
 * Commit the new take first. The SQL RPC atomically inserts the row, moves the
 * active share row, and deletes old rows; only its returned old paths are then
 * removed from Storage. RPC failure leaves prior files and share state alone.
 */
export async function saveUploadedRecordingVersion(
  client: unknown,
  args: {
    p_page_number: string;
    p_file_path: string;
    p_mime_type: string;
    p_duration_seconds: number;
  },
): Promise<SavedRecording> {
  const db = asLifecycleClient(client);
  let rpcResult: { data: unknown; error: { message: string } | null };
  try {
    rpcResult = await db.rpc("save_recording_version_with_cleanup", args);
  } catch (rpcFailure) {
    return recoverCommittedTakeOrThrow(db, args.p_file_path, rpcFailure);
  }
  if (rpcResult.error) {
    return recoverCommittedTakeOrThrow(db, args.p_file_path, rpcResult.error);
  }

  const payload = Array.isArray(rpcResult.data) ? rpcResult.data[0] : rpcResult.data;
  const result = payload as {
    recording?: SavedRecording;
    deleted_file_paths?: unknown;
    id?: string;
    file_path?: string;
    mime_type?: string;
    duration_seconds?: number;
  } | null;
  const recording = result && result.recording ? result.recording : result;
  if (!recording?.id || !recording.file_path) {
    return recoverCommittedTakeOrThrow(db, args.p_file_path, new Error("The saved recording could not be confirmed."));
  }

  const deletedFilePaths = result && Array.isArray(result.deleted_file_paths)
    ? result.deleted_file_paths.filter((path): path is string => typeof path === "string")
    : [];
  try {
    await removeReplacedAudio(client, deletedFilePaths, recording.file_path);
  } catch (cleanupError) {
    console.error("Previous recording audio cleanup failed after replacement; check the read-only orphan report.", cleanupError);
  }

  return recording as SavedRecording;
}

async function recoverCommittedTakeOrThrow(
  db: SupabaseLifecycleClient,
  filePath: string,
  originalFailure: unknown,
): Promise<SavedRecording> {
  try {
    const { data, error } = await db
      .from("recordings")
      .select("id,file_path,mime_type,duration_seconds")
      .eq("file_path", filePath)
      .eq("is_current", true)
      .maybeSingle();
    if (error) throw new RecordingSaveOutcomeUnknownError();
    if (!error && data?.id && data.file_path === filePath) {
      console.warn("The replacement recording committed, but its version-cleanup paths were not returned; check the read-only orphan report.");
      return data as SavedRecording;
    }
  } catch {
    // A lost response is ambiguous. Keep the uploaded object rather than risk
    // deleting a take that the database did commit; the orphan report catches it.
    throw new RecordingSaveOutcomeUnknownError();
  }
  throw originalFailure;
}
